export const breakpoints = {
  mobile: 320,
  mobileLarge: 480,
  tablet: 768,
  tabletLarge: 1024,
  desktop: 1280,
  desktopLarge: 1920
} as const;

export type BreakpointKey = keyof typeof breakpoints;

export interface BreakpointConfig {
  name: BreakpointKey;
  minWidth: number;
  maxWidth?: number;
  mediaQuery: string;
}

export const breakpointConfigs: Record<BreakpointKey, BreakpointConfig> = {
  mobile: {
    name: 'mobile',
    minWidth: breakpoints.mobile,
    maxWidth: breakpoints.mobileLarge - 1,
    mediaQuery: `(min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.mobileLarge - 1}px)`
  },
  mobileLarge: {
    name: 'mobileLarge',
    minWidth: breakpoints.mobileLarge,
    maxWidth: breakpoints.tablet - 1,
    mediaQuery: `(min-width: ${breakpoints.mobileLarge}px) and (max-width: ${breakpoints.tablet - 1}px)`
  },
  tablet: {
    name: 'tablet',
    minWidth: breakpoints.tablet,
    maxWidth: breakpoints.tabletLarge - 1,
    mediaQuery: `(min-width: ${breakpoints.tablet}px) and (max-width: ${breakpoints.tabletLarge - 1}px)`
  },
  tabletLarge: {
    name: 'tabletLarge',
    minWidth: breakpoints.tabletLarge,
    maxWidth: breakpoints.desktop - 1,
    mediaQuery: `(min-width: ${breakpoints.tabletLarge}px) and (max-width: ${breakpoints.desktop - 1}px)`
  },
  desktop: {
    name: 'desktop',
    minWidth: breakpoints.desktop,
    maxWidth: breakpoints.desktopLarge - 1,
    mediaQuery: `(min-width: ${breakpoints.desktop}px) and (max-width: ${breakpoints.desktopLarge - 1}px)`
  },
  desktopLarge: {
    name: 'desktopLarge',
    minWidth: breakpoints.desktopLarge,
    mediaQuery: `(min-width: ${breakpoints.desktopLarge}px)`
  }
};

// Utility functions for breakpoint logic
export const breakpointUtils = {
  // Check if current width matches a breakpoint
  isBreakpoint: (width: number, breakpoint: BreakpointKey): boolean => {
    const config = breakpointConfigs[breakpoint];
    if (config.maxWidth) {
      return width >= config.minWidth && width <= config.maxWidth;
    }
    return width >= config.minWidth;
  },

  // Get current breakpoint based on width
  getCurrentBreakpoint: (width: number): BreakpointKey => {
    if (width >= breakpoints.desktopLarge) return 'desktopLarge';
    if (width >= breakpoints.desktop) return 'desktop';
    if (width >= breakpoints.tabletLarge) return 'tabletLarge';
    if (width >= breakpoints.tablet) return 'tablet';
    if (width >= breakpoints.mobileLarge) return 'mobileLarge';
    return 'mobile';
  },

  // Check if breakpoint is mobile-like
  isMobileBreakpoint: (breakpoint: BreakpointKey): boolean => {
    return breakpoint === 'mobile' || breakpoint === 'mobileLarge';
  },

  // Check if breakpoint is tablet-like
  isTabletBreakpoint: (breakpoint: BreakpointKey): boolean => {
    return breakpoint === 'tablet' || breakpoint === 'tabletLarge';
  },

  // Check if breakpoint is desktop-like
  isDesktopBreakpoint: (breakpoint: BreakpointKey): boolean => {
    return breakpoint === 'desktop' || breakpoint === 'desktopLarge';
  },

  // Get breakpoint range
  getBreakpointRange: (breakpoint: BreakpointKey): { min: number; max?: number } => {
    const config = breakpointConfigs[breakpoint];
    return {
      min: config.minWidth,
      max: config.maxWidth
    };
  },

  // Check if width is at least a certain breakpoint
  isAtLeast: (width: number, breakpoint: BreakpointKey): boolean => {
    return width >= breakpoints[breakpoint];
  },

  // Check if width is at most a certain breakpoint
  isAtMost: (width: number, breakpoint: BreakpointKey): boolean => {
    const config = breakpointConfigs[breakpoint];
    return config.maxWidth ? width <= config.maxWidth : true;
  },

  // Get next larger breakpoint
  getNextBreakpoint: (current: BreakpointKey): BreakpointKey | null => {
    const order: BreakpointKey[] = ['mobile', 'mobileLarge', 'tablet', 'tabletLarge', 'desktop', 'desktopLarge'];
    const currentIndex = order.indexOf(current);
    return currentIndex < order.length - 1 ? order[currentIndex + 1] : null;
  },

  // Get next smaller breakpoint
  getPrevBreakpoint: (current: BreakpointKey): BreakpointKey | null => {
    const order: BreakpointKey[] = ['mobile', 'mobileLarge', 'tablet', 'tabletLarge', 'desktop', 'desktopLarge'];
    const currentIndex = order.indexOf(current);
    return currentIndex > 0 ? order[currentIndex - 1] : null;
  },

  // Generate CSS media queries
  generateMediaQuery: (breakpoint: BreakpointKey): string => {
    return breakpointConfigs[breakpoint].mediaQuery;
  },

  // Generate min-width media query
  generateMinWidthQuery: (breakpoint: BreakpointKey): string => {
    return `(min-width: ${breakpoints[breakpoint]}px)`;
  },

  // Generate max-width media query
  generateMaxWidthQuery: (breakpoint: BreakpointKey): string => {
    const config = breakpointConfigs[breakpoint];
    const maxWidth = config.maxWidth || breakpoints[breakpoint];
    return `(max-width: ${maxWidth}px)`;
  },

  // Generate between breakpoints media query
  generateBetweenQuery: (minBreakpoint: BreakpointKey, maxBreakpoint: BreakpointKey): string => {
    return `(min-width: ${breakpoints[minBreakpoint]}px) and (max-width: ${breakpoints[maxBreakpoint] - 1}px)`;
  }
};

// Responsive value type helper
export type ResponsiveValue<T> = T | Partial<Record<BreakpointKey, T>>;

// Utility to resolve responsive values
export const resolveResponsiveValue = <T>(
  value: ResponsiveValue<T>,
  currentBreakpoint: BreakpointKey
): T => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const breakpointValues = value as Partial<Record<BreakpointKey, T>>;
    
    // Check current breakpoint first
    if (breakpointValues[currentBreakpoint]) {
      return breakpointValues[currentBreakpoint];
    }
    
    // Fall back to smaller breakpoints
    const order: BreakpointKey[] = ['mobile', 'mobileLarge', 'tablet', 'tabletLarge', 'desktop', 'desktopLarge'];
    const currentIndex = order.indexOf(currentBreakpoint);
    
    for (let i = currentIndex; i >= 0; i--) {
      const breakpoint = order[i];
      if (breakpointValues[breakpoint]) {
        return breakpointValues[breakpoint];
      }
    }
    
    // If no smaller breakpoint found, try larger ones
    for (let i = currentIndex + 1; i < order.length; i++) {
      const breakpoint = order[i];
      if (breakpointValues[breakpoint]) {
        return breakpointValues[breakpoint];
      }
    }
    
    // This shouldn't happen if the responsive value is properly typed
    throw new Error(`No value found for breakpoint ${currentBreakpoint}`);
  }
  
  return value;
};

// Tailwind CSS breakpoint classes
export const tailwindBreakpoints = {
  mobile: '',
  mobileLarge: 'sm:',
  tablet: 'md:',
  tabletLarge: 'lg:',
  desktop: 'xl:',
  desktopLarge: '2xl:'
} as const;

// Helper to generate Tailwind classes with breakpoints
export const generateTailwindClasses = (
  classes: ResponsiveValue<string>
): string => {
  if (typeof classes === 'string') {
    return classes;
  }
  
  const breakpointClasses = classes;
  const result: string[] = [];
  
  Object.entries(breakpointClasses).forEach(([breakpoint, className]) => {
    if (className) {
      const prefix = tailwindBreakpoints[breakpoint as BreakpointKey];
      result.push(`${prefix}${className}`);
    }
  });
  
  return result.join(' ');
};